/// <amd-module name="VSSUI/Components/Coin/Coin" />
import * as React from "react";
import { Tooltip } from "VSSUI/TooltipEx";
import { css, KeyCode } from "VSSUI/Util";
import { getColorString, IColor } from "VSSUI/Utilities/Color";
import { getInitialsColorFromName, getInitialsFromName } from "./Coin.Initials";
import { ICoinProps } from "./Coin.Props";

export interface ICoinState {
    imageError: boolean;
    imageLoaded: boolean;
    showIdentityCard: boolean;
}

/**
 * Renders a user's profile/identity/avatar image.
 */
export class Coin extends React.Component<ICoinProps, ICoinState> {
    constructor(props: ICoinProps) {
        super(props);

        this.state = {
            imageError: false,
            imageLoaded: false,
            showIdentityCard: false
        };
    }

    public render(): JSX.Element {
        const { ariaLabel, className, displayName, dataIsFocusable, onClick, isTabStop, imgAltText = "", imageUrl, role, size, tooltipProps } = this.props;

        const sizeClass = "size" + size;

        // Set the focus and aria-expand attributes based on props passed
        const additionalAttributes: { [key: string]: any } = {};
        if (dataIsFocusable) {
            additionalAttributes["data-is-focusable"] = true;
        }
        if (isTabStop) {
            additionalAttributes["tabIndex"] = 0;
        }

        additionalAttributes["aria-label"] = ariaLabel !== undefined ? ariaLabel : displayName;

        // Setting the aria related properties and user action delegates only if there is a renderIdentityCard callback
        if (onClick) {
            additionalAttributes["onKeyDown"] = this.handleKeyDown;
            additionalAttributes["onClick"] = onClick;
            additionalAttributes["role"] = "button";
        }

        if (role === "presentation") {
            additionalAttributes["role"] = "presentation";
            // Remove aria-label if we're setting role to presentation.
            additionalAttributes["aria-label"] = "";
        }

        const backgroundColor: IColor | undefined = getInitialsColorFromName(displayName);
        const initialsIdentity = (
            <div className={css("bolt-coin-content", sizeClass)} style={backgroundColor && { background: getColorString(backgroundColor) }}>
                <span>{getInitialsFromName(displayName)}</span>
            </div>
        );

        const imageElement: JSX.Element =
            imageUrl !== undefined && !this.state.imageError ? (
                <>
                    <img
                        className={css("bolt-coin-content using-image", sizeClass, !this.state.imageLoaded && "pending-load-image")}
                        src={imageUrl}
                        alt={role === "presentation" ? "" : imgAltText}
                        onError={this.onImageError}
                        onLoad={this.onLoad}
                    />
                </>
            ) : (
                initialsIdentity
            );

        // Getting the reference to the div around the image because the Callout within IdentityCard has positioning problems in some cases when passing in img element as the target
        const content = (
            <div className={css("bolt-coin flex-noshrink", className, sizeClass, onClick && "cursor-pointer")} {...additionalAttributes}>
                {imageElement}
            </div>
        );

        if (tooltipProps !== null) {
            return (
                <Tooltip text={displayName} showOnFocus={true} {...tooltipProps}>
                    {content}
                </Tooltip>
            );
        } else {
            return content;
        }
    }

    private onImageError = (event: React.SyntheticEvent<HTMLImageElement, Event>): void => {
        this.setState({ imageError: true });
    };

    private onLoad = (event: React.SyntheticEvent<HTMLImageElement, Event>): void => {
        this.setState({ imageLoaded: true });
    };

    private handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
        if (e.keyCode === KeyCode.enter || e.keyCode === KeyCode.space) {
            this.props.onClick && this.props.onClick();
        }
    };
}
